import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Receipt,
  CalendarDays,
  LogOut,
  X,
  Globe,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { logout, user } = useAuth();

  const menuItems = [
    {
      path: '/admin',
      icon: LayoutDashboard,
      label: 'Dashboard',
      exact: true,
    },
    {
      path: '/admin/usuarios',
      icon: Users,
      label: 'Usuários',
    },
    {
      path: '/admin/associados',
      icon: UserCheck,
      label: 'Associados',
    },
    {
      path: '/admin/despesas',
      icon: Receipt,
      label: 'Despesas',
    },
    {
      path: '/admin/calendario',
      icon: CalendarDays,
      label: 'Calendário',
    },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-brand-brown border-r border-brand-darkBrown transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-brand-darkBrown">
            <Link to="/admin" className="flex items-center">
              <img 
                src="/assets/logo-branco.png" 
                alt="Ilê Asé Sòpònnón" 
                className="h-10 w-auto"
              />
            </Link>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-brand-darkBrown"
            >
              <X className="h-5 w-5 text-brand-cream" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-brand-darkBrown">
            <div className="flex items-center space-x-3">
              {user?.photo_url ? (
                <img
                  src={user.photo_url}
                  alt={user.name}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div className="h-12 w-12 bg-brand-gold rounded-full flex items-center justify-center">
                  <span className="text-white font-display font-bold text-lg">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brand-cream truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-brand-beige truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    active
                      ? 'bg-brand-gold text-white shadow-md'
                      : 'text-brand-cream hover:bg-brand-darkBrown'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-semibold">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer actions */}
          <div className="p-4 border-t border-brand-darkBrown space-y-2">
            <Link
              to="/"
              onClick={onClose}
              className="flex items-center space-x-3 px-4 py-3 rounded-xl text-brand-cream hover:bg-brand-darkBrown w-full transition-colors"
            >
              <Globe className="h-5 w-5" />
              <span className="font-semibold">Voltar ao Site</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-4 py-3 rounded-xl text-accent-red hover:bg-brand-darkBrown w-full transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-semibold">Sair</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

